import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, forkJoin, fromEvent, of, retry } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { navigate } from '../../../actions/core.actions';
import { RoutingPathResolver } from '../../../app-routing-path-resolver';
import { ThemeForDisplay } from '../../../models/playlist';
import {
  ExamPaper,
  ReadableEnglishPlaylistProblem,
  ReadableEnglishProblem,
  ReadableHistoryPlaylistProblem,
  ReadableHistoryProblem,
  ReadableNationalLanguagePlaylistProblem,
  ReadableNationalLanguageProblem,
  ReadableProblem,
  ReadableSciencePlaylistProblem,
  ReadableScienceProblem
} from '../../../models/problem';
import { RootState } from '../../../reducers';
import { GA_EVENT_ACTIONS, GA_EVENT_CATEGORIES, GA_EVENT_LABELS } from '../../../resources/common-id/ga';
import { SCIENCE_IDS, SubjectId } from '../../../resources/config';
import { ScrollDirectionService } from '../../../services/scroll-direction.service';
import { GAUtil } from '../../../utils/ga-util';
import { Log } from '../../../utils/log';
import { ProblemImageUtil } from '../../../utils/problem-image-util';
import { mergeConsecutiveLayoutType3, splitImageItemsByMixedLayout } from '../../../utils/mixed-layout-util';
import { ButtonTogglePaperBookmarkComponent } from '../button-toggle-paper-bookmark/button-toggle-paper-bookmark.component';
import { ButtonToggleSavedProblemComponent } from '../button-toggle-saved-problem/button-toggle-saved-problem.component';
import { Router, ActivatedRoute } from '@angular/router';
import { PREMIUM_MODAL_DISPLAY_PATTERN, IS_THREE_MONTH_MODE } from 'src/app/resources/common-id/premium-modal-display-pattern';
import { CommonIdSubjectUtil } from 'src/app/utils/common-id/common-id-subject-util';
import { StaticCommonData } from 'src/app/models/static-common-data';

interface ProblemImage {
  url: string;
  width: number;
  height: number;
}

interface ImageItem {
  readableProblemId: string;
  layoutType?: number;
  fImage?: ProblemImage;
  problemImages?: ProblemImage[];
}

type ProblemId = string;

@Component({
  selector: 'app-problem-detail-frame',
  templateUrl: './problem-detail-frame.component.html',
  styleUrls: ['./problem-detail-frame.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProblemDetailFrameComponent implements OnChanges {
  @Input() staticCommonData: StaticCommonData;
  @Input() readableEnglishProblems: ReadableEnglishProblem[];
  @Input() readableScienceProblems: ReadableScienceProblem[];
  @Input() readableNationalLanguageProblems: ReadableNationalLanguageProblem[];
  @Input() readableJapaneseHistoryProblems: ReadableHistoryProblem[];
  @Input() readableWorldHistoryProblems: ReadableHistoryProblem[];
  @Input() englishPlaylistTheme: ThemeForDisplay<ReadableEnglishPlaylistProblem>;
  @Input() sciencePlaylistTheme: ThemeForDisplay<ReadableSciencePlaylistProblem>;
  @Input() nationalLanguagePlaylistTheme: ThemeForDisplay<ReadableNationalLanguagePlaylistProblem>;
  @Input() japaneseHistoryPlaylistTheme: ThemeForDisplay<ReadableHistoryPlaylistProblem>;
  @Input() worldHistoryPlaylistTheme: ThemeForDisplay<ReadableHistoryPlaylistProblem>;
  @Input() paperMode: boolean;
  @Input() tryMode: boolean;
  @Input() trialMode: boolean;
  @Input() canDisplay = true;
  @Input() subjectId: string;
  @Input() hidePaperProblemBtn: boolean;
  @Input() showPaperBookmarkBtn: boolean;
  @Input() showComment: boolean;
  @Input() showSaveAnsweredBtn: boolean;
  @Input() disabledSaveAnsweredBtn: boolean;
  @Input() canBookmarkSpinner = false;
  @Input() isPaperBookmarked = false;
  @Input() isAnswered = false;
  @Input() tryDaimonId: string;
  @Input() menuType: 'article' | 'spellcheck' = 'article';
  // お気に入りリストの印刷で使用する変数
  @Input() isFromBookmark = false;

  // おすすめ問題かどうかを判断するフラグ
  @Input() isFromPlaylist = false;

  @Output() menuTypeChangeHandler = new EventEmitter();
  @Output() paperClickHandler = new EventEmitter();
  @Output() printClickHandler = new EventEmitter();

  @Output() addPaperBookmarkClick = new EventEmitter<ProblemId>();
  @Output() deletePaperBookmarkClick = new EventEmitter<ProblemId>();
  @Output() saveAnsweredClick = new EventEmitter<ProblemId>();
  @Output() openAnsweredDialogClick = new EventEmitter();

  playlistTheme:
    | ThemeForDisplay<ReadableEnglishPlaylistProblem>
    | ThemeForDisplay<ReadableSciencePlaylistProblem>
    | ThemeForDisplay<ReadableNationalLanguagePlaylistProblem>
    | ThemeForDisplay<ReadableHistoryPlaylistProblem>;
  readablePlaylistProblem:
    | ReadableEnglishPlaylistProblem
    | ReadableSciencePlaylistProblem
    | ReadableNationalLanguagePlaylistProblem
    | ReadableHistoryPlaylistProblem;
  loaded$: Observable<boolean> = of(false);
  status$: Observable<boolean> = of(true);
  problemId: string;
  //menuType: 'article' | 'spellcheck' = 'article';
  layoutType: number;
  readableProblem: ReadableProblem; // お気に入り以外の印刷で使用する変数
  readableProblems: ReadableProblem[]; // お気に入りの印刷で使用する変数
  subjectName: string;
  examDate: string;
  nyushiName: string;
  examTime: string;
  specialInfoData: string[];
  fImage: ProblemImage;
  problemImages: ProblemImage[];
  hasRunOnce: boolean; // 表紙データの設定をする際に使用
  private LOG_SOURCE = this.constructor.name;
  private daimonId: string | null;

  scrollDirectionIsUp = this.scrollDirectionService.scrollDirectionIsUp;

  isThreeMonthMode: boolean;
  displayOption: string;
  NORMAL_A: string;
  NORMAL_B: string;
  THREE_MONTH_A: string;
  THREE_MONTH_B: string;

  // お気に入りリストの印刷で使用する変数
  imageItems$: Observable<ImageItem[]> = of([]);
  imageItems: ImageItem[] = [];

  @ViewChild('saveAnsweredBtn') buttonToggleAnsweredProblemComponent: ButtonToggleSavedProblemComponent;
  @ViewChild('savePaperBtn') buttonTogglePaperBookmarkComponent: ButtonTogglePaperBookmarkComponent;

  constructor(
    private store: Store<RootState>,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private scrollDirectionService: ScrollDirectionService,
    private changeDetectorRefs: ChangeDetectorRef
  ) {
    this.NORMAL_A = PREMIUM_MODAL_DISPLAY_PATTERN.NORMAL_A;
    this.NORMAL_B = PREMIUM_MODAL_DISPLAY_PATTERN.NORMAL_B;
    this.THREE_MONTH_A = PREMIUM_MODAL_DISPLAY_PATTERN.THREE_MONTH_A;
    this.THREE_MONTH_B = PREMIUM_MODAL_DISPLAY_PATTERN.THREE_MONTH_B;
  }

  ngOnChanges(changes): void {
    this.imageItems = [];

    // ３カ月用プレミアムモダルと通常用の切り替えフラグ
    this.isThreeMonthMode = IS_THREE_MONTH_MODE;

    // ランダムにデザインを選択する処理
    this.selectDisplayOption();

    // TODO あとで解くの登録/解除を実行した場合に isPaperBookmarked が更新され ngOnChanges が呼び出されてしまう
    // TODO 解答済みの登録/解除を実行した場合に isAnswered が更新され ngOnChanges が呼び出されてしまう
    if (
      (changes.isPaperBookmarked !== undefined && Object.keys(changes).length === 1) ||
      (changes.isAnswered !== undefined && Object.keys(changes).length === 1)
    ) {
      return;
    }

    this.setUpReadableProblemBySubjectId();
    // 問題詳細画面でログアウトを実行した際にエラーが発生する場合があるため
    if (!this.readableProblem) return null;

    if (this.isFromPlaylist) this.setUpPlaylistThemeBySubjectId();

    this.hasRunOnce = false;
    for (const readableProblem of this.readableProblems) {
      if (readableProblem.questionLayout !== undefined && readableProblem.answerLayout !== undefined) {
        this.setUpDataForEachProblem(readableProblem);
      }
    }
  }

  resetToggleAnsweredProblemBtn() {
    this.buttonToggleAnsweredProblemComponent.reset();
    this.changeDetectorRefs.detectChanges();
  }

  resetTogglePaperBookmarkBtn() {
    this.buttonTogglePaperBookmarkComponent.reset();
    this.changeDetectorRefs.detectChanges();
  }

  menuClickHandler(key) {
    this.menuType = key;
    this.menuTypeChangeHandler.emit(key);
    this.imageItems = []; // 印刷用データの初期化
    for (const readableProblem of this.readableProblems) {
      if (readableProblem.questionLayout !== undefined && readableProblem.answerLayout !== undefined) {
        this.setUpDataForEachProblem(readableProblem);
      }
    }
  }

  calcImageLayout(layoutType: number, images: ProblemImage[]): Array<ProblemImage[]> {
    if (!images) return;
    const layoutImages: Array<ProblemImage[]> = [];
    let containers: ProblemImage[] = [];
    let daimon = '';

    switch (layoutType) {
      case 1: {
        images.map((image, index) => {
          const currentDaimon: string = this.getDaimonId(image.url);
          if (daimon === '') daimon = currentDaimon;
          if (currentDaimon === undefined || daimon !== currentDaimon) {
            daimon = currentDaimon;

            layoutImages.push(containers);
            containers = [];
          }
          containers.push(image);

          if (index === images.length - 1) {
            layoutImages.push(containers);
          }
        });
        break;
      }
      case 2: {
        let width = 0;
        const maxWidth = Number(String(getComputedStyle(document.querySelector('.pd-layout')).getPropertyValue('--pd-max-width')).trim());

        images.map((image, index) => {
          const currentDaimon: string = this.getDaimonId(image.url);
          if (daimon === '') daimon = currentDaimon;

          if (width > maxWidth || currentDaimon === undefined || daimon !== currentDaimon) {
            daimon = currentDaimon;

            layoutImages.push(containers);
            containers = [];
            width = 0;
          }

          width += image.width;
          containers.push(image);

          if (index === images.length - 1) {
            layoutImages.push(containers);
          }
        });
        break;
      }
      case 3: {
        let i = 0;
        images.map((image, index) => {
          if (i === 2) {
            layoutImages.push(containers);
            containers = [];
            i = 0;
          }
          i += 1;
          containers.push(image);

          if (index === images.length - 1) {
            layoutImages.push(containers);
          }
        });
        break;
      }
      default:
    }

    return layoutImages;
  }

  gotoPlan() {
    const searchByCategoriesDetailPath = `${RoutingPathResolver.resolveCommonIdSearchByCategories()}/`;
    const eventLabel =
      this.router.url.indexOf(searchByCategoriesDetailPath) !== -1
        ? GA_EVENT_LABELS.SEARCH_BY_CATEGORIES_DETAIL
        : GA_EVENT_LABELS.PROBLEM_DETAIL;
    const eventParams = {
      'event_category': GA_EVENT_CATEGORIES.PREMIUM_PLAN_VIEW,
      'event_label': eventLabel,
      'value': 1
    };
    GAUtil.sendEvent(GA_EVENT_ACTIONS.CLICK, eventParams);

    const url = RoutingPathResolver.resolveCommonIdPurchase();
    this.store.dispatch(navigate({ url, extras: { queryParams: { utm_source: this.displayOption } } }));
  }

  private getDaimonId(url: string): string {
    const daimonId: string | undefined = url.match('.+/(.+?).[a-z]+([?#;].*)?$')[1].split('_')[1];
    return daimonId
      ? daimonId
      : Math.random()
          .toString(32)
          .substring(2);
  }

  private getImage(imageUrl: string): Observable<ImageData | Error> {
    return new Observable<any>(observer => {
      const image = new Image();
      fromEvent(image, 'load')
        .pipe(take(1))
        .subscribe(e => {
          // Log.debug(this.LOG_SOURCE, `load success:`, e.target);
          observer.next(e.target);
          observer.complete();
        });
      fromEvent(image, 'error')
        .pipe(take(1))
        .subscribe(error => {
          // Log.debug(this.LOG_SOURCE, `load error:`, error);
          observer.error(error);
          image.remove();
        });
      image.src = imageUrl;
    });
  }

  private setUpReadableProblemBySubjectId() {
    // 科目ごとの問題リストをマッピング
    const problemMap = {
      [SubjectId.ENGLISH]: this.readableEnglishProblems,
      [SubjectId.NATIONAL_LANGUAGE]: this.readableNationalLanguageProblems,
      [SubjectId.JAPANESE_HISTORY]: this.readableJapaneseHistoryProblems,
      [SubjectId.WORLD_HISTORY]: this.readableWorldHistoryProblems
    };

    if (SCIENCE_IDS.includes(this.subjectId)) {
      this.readableProblems = this.readableScienceProblems;
    } else {
      this.readableProblems = problemMap[this.subjectId as keyof typeof problemMap] || null;
    }

    // 先頭の問題をセット
    this.readableProblem = this.readableProblems?.[0] || null;

    if (!this.readableProblem) return;

    Log.debug(this.LOG_SOURCE, `readableProblem:`, this.readableProblem);
  }

  private setUpPlaylistThemeBySubjectId() {
    // URLより該当の問題IDを取得
    this.problemId = this.activatedRoute.snapshot.paramMap.get('problemId') || '';

    // 科目ごとの playlistTheme をマッピング
    const themeMap = {
      [SubjectId.ENGLISH]: this.englishPlaylistTheme,
      [SubjectId.NATIONAL_LANGUAGE]: this.nationalLanguagePlaylistTheme,
      [SubjectId.JAPANESE_HISTORY]: this.japaneseHistoryPlaylistTheme,
      [SubjectId.WORLD_HISTORY]: this.worldHistoryPlaylistTheme
    };

    if (SCIENCE_IDS.includes(this.subjectId)) {
      this.playlistTheme = this.sciencePlaylistTheme;
    } else {
      this.playlistTheme = themeMap[this.subjectId as keyof typeof themeMap] || null;
    }

    // 該当問題を検索
    this.readablePlaylistProblem = this.playlistTheme ? this.playlistTheme.problems.find(p => p.id === this.problemId) : null;
  }

  private setUpDataForCover() {
    const paperId = this.problemId.slice(0, -2);
    this.subjectName = CommonIdSubjectUtil.getSubjectName(paperId, this.staticCommonData.subjects);
  }

  private setUpDataForEachProblem(readableProblem: ReadableProblem) {
    this.problemId = readableProblem.id;

    // 表紙データの設定は1度のみ
    if (!this.hasRunOnce) this.setUpDataForCover();
    this.hasRunOnce = true;

    this.layoutType = this.menuType === 'article' ? readableProblem.questionLayout.layoutType : readableProblem.answerLayout.layoutType;
    this.imageItems.push({ readableProblemId: this.problemId, layoutType: this.layoutType });

    if (this.tryDaimonId) {
      this.daimonId = this.tryDaimonId;
    } else if (this.trialMode) {
      // 体験版プランで大問単位表示の際は１つ目の大問を固定で表示する
      this.daimonId = !this.paperMode ? '01' : null;
    } else {
      this.daimonId = !this.paperMode ? this.problemId.slice(-2) : null;
    }

    const fImageUrl: string = environment.cloudfrontHost + ProblemImageUtil.getPath(readableProblem.fImage, this.trialMode);
    const examPaper: ExamPaper = readableProblem.examPaper;
    this.examDate = examPaper && examPaper.examDate ? examPaper.examDate : '-';
    this.nyushiName = examPaper && examPaper.nyushiName ? examPaper.nyushiName : '-';
    this.examTime = examPaper && examPaper.examTime ? examPaper.examTime : '-';
    this.specialInfoData = [
      examPaper && examPaper.bikou ? `〔備考〕${examPaper.bikou}` : '',
      examPaper && examPaper.nyushiKamoku ? `〔入試科目〕${examPaper.nyushiKamoku}` : '',
      examPaper && examPaper.chuui ? `〔注意〕${examPaper.chuui}` : ''
    ].filter(Boolean);

    // 体験版ではf画像を使用ない
    if (!this.trialMode && this.paperMode && examPaper?.chkFlg === '1' && readableProblem.fImage !== '') {
      this.getImage(fImageUrl)
        .pipe(retry(3))
        .pipe(
          take(1),
          catchError(error => {
            Log.debug(this.LOG_SOURCE, `load failed:`, error);
            return of(null);
          })
        )
        .subscribe((image: ImageData) => {
          const isRetina = fImageUrl.includes('@2x');
          this.fImage = {
            width: isRetina ? image.width / 2 : image.width,
            height: isRetina ? image.height / 2 : image.height,
            url: fImageUrl
          };

          // 印刷画面の問題情報を保持する配列に問題画像の情報を追加する
          const index = this.imageItems.findIndex(item => item.readableProblemId === readableProblem.id);
          if (index !== -1) {
            this.imageItems[index].fImage = this.fImage;
          }

          this.setUpProblemImages(readableProblem);
        });
    } else {
      this.setUpProblemImages(readableProblem);
    }
  }

  private setUpProblemImages(readableProblem: ReadableProblem) {
    this.loaded$ = of(false);
    this.status$ = of(true);

    const targetImages: string[] = this.menuType === 'article' ? readableProblem.questionImages : readableProblem.answerImages;
    if (targetImages === undefined || targetImages.length === 0) {
      this.loaded$ = of(true);
      this.status$ = of(false);
      return;
    }

    const daimonHead: string = this.menuType === 'article' ? 'q' : 'a';
    const allUrls = targetImages.map(img => environment.cloudfrontHost + ProblemImageUtil.getPath(img, this.trialMode));
    const imageUrls: string[] = this.isFromBookmark
      ? allUrls
      : allUrls.filter(url => this.daimonId === null || url.includes(daimonHead + this.daimonId));
    if (imageUrls.length === 0) {
      this.loaded$ = of(true);
      this.status$ = of(false);
      return;
    }

    const requests$: Observable<ImageData | Error>[] = imageUrls.map(url => this.getImage(url));
    forkJoin(requests$.map(request => request.pipe(retry(3))))
      .pipe(
        take(1),
        catchError(error => {
          Log.debug(this.LOG_SOURCE, `load failed:`, error);
          return of(null);
        })
      )
      .subscribe((images: ImageData[]) => {
        Log.debug(this.LOG_SOURCE, `images:`, images);
        if (images === null) {
          this.status$ = of(false);
        } else {
          this.problemImages = images.map((image: ImageData, i) => {
            const isRetina = imageUrls[i].includes('@2x');
            return {
              width: isRetina ? image.width / 2 : image.width,
              height: isRetina ? image.height / 2 : image.height,
              url: imageUrls[i]
            };
          });
        }

        // 印刷画面の問題情報を保持する配列に問題画像の情報を追加する
        if (this.isFromBookmark && this.trialMode) {
          const problemImagesGroupedByDaimonId = this.trialProblemImagesGroupedByDaimonId(this.menuType);

          const readableProblemId = Number(this.imageItems[0].readableProblemId);
          const layoutType = this.imageItems[0].layoutType;
          // 大問ごとにデータを作成する（大問ごとに改ページするため）
          this.imageItems = problemImagesGroupedByDaimonId.map((image, index) => {
            return {
              readableProblemId: index === 0 ? String(readableProblemId) : String(readableProblemId + index),
              layoutType,
              problemImages: image
            };
          });
        } else {
          const index = this.imageItems.findIndex(item => item.readableProblemId === readableProblem.id);
          if (index !== -1) {
            this.imageItems[index].problemImages = this.problemImages;
          }
        }
        // レイアウト混在 paper のみ daimon 単位に分割し、画像名 _l\d から layoutType を割り当てる（混在無しは noop）
        this.imageItems = splitImageItemsByMixedLayout(this.imageItems, this.menuType);
        // 画面表示では連続する layoutType=3 大問を 1 ブロックに結合し、従来の表示ルール（2列+内部 separater）に戻す
        this.imageItems = mergeConsecutiveLayoutType3(this.imageItems);
        this.imageItems$ = of(this.imageItems);

        this.loaded$ = of(true);
        this.changeDetectorRefs.detectChanges();
      });
  }

  private trialProblemImagesGroupedByDaimonId(menuType: 'article' | 'spellcheck'): ProblemImage[][] {
    // 体験版の画像情報を、問題IDごとの配列にする
    return Object.values(
      this.problemImages.reduce((acc, item) => {
        const match = menuType === 'article' ? item.url.match(/_q\d{2}_/) : item.url.match(/_a\d{2}_/); // captures "_q01_", "_q02_", etc.
        const key = match ? match[0] : 'unknown';

        if (!acc[key]) acc[key] = [];
        acc[key].push(item);

        return acc;
      }, {} as ProblemImage)
    );
  }

  private selectDisplayOption() {
    let options: string[];
    if (this.isThreeMonthMode) {
      options = [this.THREE_MONTH_A, this.THREE_MONTH_B];
    } else {
      options = [this.NORMAL_A, this.NORMAL_B];
    }
    this.displayOption = this.getRandomOption(options);
  }

  private getRandomOption(options: string[]): string {
    const randomIndex = Math.floor(Math.random() * options.length);
    return options[randomIndex];
  }
}
